import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { emailRegex } from '../../config_and_helpers/config';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  formatOrderContactUpdatedAt,
  formatOrderContactUpdatedBy,
  getOrderContactSettings,
  mapOrderContactSettingsErrors,
  updateOrderContactSettings,
} from '../../clients/order_request_client';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';

const LOG_CONTEXT = 'GlobalOrderContactModal';

const EMPTY_ERRORS = {
  contact_name: [],
  contact_email: [],
  non_field_errors: [],
};

/**
 * Lab contact used on supplier quote emails and reorder alerts.
 * Yellow glass modal, same chrome as the other Procure forms.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {string} props.token
 * @param {function} [props.onSettingsUpdated]
 */
const GlobalOrderContactModal = ({ open, onClose, token, onSettingsUpdated }) => {
  const isViewer = useIsLabViewer();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(EMPTY_ERRORS);

  useEffect(() => {
    if (!open || !token || isViewer) return;
    const load = async () => {
      setIsLoading(true);
      setFetchError('');
      setFieldErrors(EMPTY_ERRORS);
      const response = await getOrderContactSettings(token);
      setIsLoading(false);
      if (response?.success) {
        setContactName(response.data?.contact_name || '');
        setContactEmail(response.data?.contact_email || '');
        setUpdatedAt(response.data?.updated_at || null);
        setUpdatedBy(response.data?.updated_by || null);
        logInfo(LOG_CONTEXT, 'lab contact loaded', {
          hasName: Boolean(response.data?.contact_name),
          hasEmail: Boolean(response.data?.contact_email),
        });
      } else {
        const message =
          response?.messages?.[0] || 'Failed to load global contact settings.';
        setFetchError(message);
        logError(LOG_CONTEXT, 'lab contact load failed', { message });
      }
    };
    load();
  }, [open, token, isViewer]);

  const validate = () => {
    const next = { ...EMPTY_ERRORS };
    const trimmedName = contactName.trim();
    const trimmedEmail = contactEmail.trim();
    if (!trimmedName) {
      next.contact_name = ['Contact name is required.'];
    } else if (trimmedName.length > 255) {
      next.contact_name = ['Contact name must be at most 255 characters.'];
    }
    if (!trimmedEmail) {
      next.contact_email = ['Contact email is required.'];
    } else if (!emailRegex.test(trimmedEmail)) {
      next.contact_email = ['Contact email must be a valid email address.'];
    }
    setFieldErrors(next);
    return next.contact_name.length === 0 && next.contact_email.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setFieldErrors(EMPTY_ERRORS);
    const payload = {
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
    };
    logInfo(LOG_CONTEXT, 'saving lab contact', {
      nameLength: payload.contact_name.length,
    });
    const response = await updateOrderContactSettings(token, payload);
    setIsSaving(false);
    if (response?.success) {
      setContactName(response.data?.contact_name || '');
      setContactEmail(response.data?.contact_email || '');
      setUpdatedAt(response.data?.updated_at || null);
      setUpdatedBy(response.data?.updated_by || null);
      onSettingsUpdated?.(response.data);
      onClose?.();
      return;
    }
    setFieldErrors(mapOrderContactSettingsErrors(response?.error));
    logError(LOG_CONTEXT, 'lab contact save failed', {
      messageCount: mapOrderContactSettingsErrors(response?.error).non_field_errors
        .length,
    });
  };

  const metadataText = useMemo(() => {
    const when = formatOrderContactUpdatedAt(updatedAt);
    const who = formatOrderContactUpdatedBy(updatedBy);
    const parts = [];
    if (when) parts.push(`Last updated: ${when}`);
    if (who) parts.push(`By: ${who}`);
    return parts.join(' · ');
  }, [updatedAt, updatedBy]);

  if (isViewer) {
    return null;
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      backdrop="static"
      dialogClassName="procure-modal-glass procure-modal-glass-form"
      aria-labelledby="global-order-contact-modal"
    >
      <Modal.Header closeButton className="procure-modal__header">
        <Modal.Title id="global-order-contact-modal">Set lab contact</Modal.Title>
      </Modal.Header>
      <Modal.Body className="procure-modal__body">
        <p className="text-muted mb-3">
          This contact is used for supplier quote-request emails and reorder alert
          emails across the lab.
        </p>

        {fetchError ? <Alert variant="danger">{fetchError}</Alert> : null}
        {fieldErrors.non_field_errors.length > 0 ? (
          <Alert variant="danger">
            {fieldErrors.non_field_errors.map((msg, idx) => (
              <div key={idx}>{msg}</div>
            ))}
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : (
          <>
            <Form.Group className="mb-3" controlId="labContactName">
              <Form.Label>Contact name</Form.Label>
              <Form.Control
                value={contactName}
                onChange={e => setContactName(e.target.value)}
                isInvalid={fieldErrors.contact_name.length > 0}
                required
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.contact_name[0]}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="labContactEmail">
              <Form.Label>Contact email</Form.Label>
              <Form.Control
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                isInvalid={fieldErrors.contact_email.length > 0}
                required
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.contact_email[0]}
              </Form.Control.Feedback>
            </Form.Group>
            {metadataText ? (
              <p className="text-muted small mb-0">{metadataText}</p>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="procure-modal__footer">
        {isSaving ? (
          <Button variant="primary" className="glass-primary-btn" disabled>
            <Spinner
              size="sm"
              as="span"
              animation="border"
              role="status"
              aria-hidden="true"
            />
          </Button>
        ) : (
          <Button
            variant="primary"
            className="glass-primary-btn"
            onClick={handleSave}
            disabled={isLoading || Boolean(fetchError)}
          >
            Save
          </Button>
        )}
        <Button
          variant="secondary"
          className="procure-modal__close-btn"
          onClick={onClose}
          disabled={isSaving}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GlobalOrderContactModal;
